import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Button, LanguageTabs, Loading, Textarea } from '../components/ui';
import { useCrud } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface Album {
  id: number;
  title: string;
  spotify_url?: string;
  meta_en?: string;
  meta_ro?: string;
  icon: string;
  note_en?: string;
  note_ro?: string;
  sort_order: number;
}

const AlbumsPage: React.FC = () => {
  const albums = useCrud<Album>('/api/albums');
  const { showToast } = useToast();
  const [editingAlbum, setEditingAlbum] = useState<Album | null>(null);

  useEffect(() => { albums.fetchAll(); }, []);

  const handleSave = async (album: Partial<Album>) => {
    if (editingAlbum?.id) {
      const result = await albums.update(editingAlbum.id, album);
      if (result) {
        showToast('Album updated!', 'success');
        setEditingAlbum(null);
      }
    } else {
      const result = await albums.create(album);
      if (result) {
        showToast('Album created!', 'success');
        setEditingAlbum(null);
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Delete this album?')) {
      const result = await albums.remove(id);
      if (result) showToast('Album deleted', 'success');
    }
  };

  if (albums.loading && albums.items.length === 0) return <Loading />;

  return (
    <>
      <PageHeader
        title="Albums"
        subtitle="Manage your discography"
        actions={
          <Button onClick={() => setEditingAlbum({ id: 0, title: '', icon: '💿', sort_order: albums.items.length })}>
            + Add Album
          </Button>
        }
      />

      <div className="admin-content">
        <Card>
          <div className="admin-list-editor">
            {albums.items.map(album => (
              <div key={album.id} className="admin-list-item">
                <span style={{ fontSize: '1.5rem' }}>{album.icon}</span>
                <div className="admin-list-item-content">
                  <div>
                    <strong>{album.title}</strong>
                    <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                      {album.meta_en || 'No metadata'}
                    </div>
                  </div>
                </div>
                <div className="admin-list-item-actions">
                  <Button size="sm" variant="secondary" onClick={() => setEditingAlbum(album)}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(album.id)}>Delete</Button>
                </div>
              </div>
            ))}
            {albums.items.length === 0 && (
              <p style={{ color: 'var(--admin-text-muted)', textAlign: 'center', padding: 20 }}>
                No albums yet. Add your first album above.
              </p>
            )}
          </div>
        </Card>

        {editingAlbum && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }} onClick={() => setEditingAlbum(null)}>
            <div style={{ background: 'var(--admin-surface)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ marginBottom: 20 }}>{editingAlbum.id ? 'Edit Album' : 'Add Album'}</h3>
              <AlbumForm
                album={editingAlbum}
                onSave={handleSave}
                onCancel={() => setEditingAlbum(null)}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const AlbumForm: React.FC<{
  album: Album;
  onSave: (a: Partial<Album>) => void;
  onCancel: () => void;
}> = ({ album, onSave, onCancel }) => {
  const [form, setForm] = useState(album);
  const [lang, setLang] = useState<'en' | 'ro'>('en');

  return (
    <div className="admin-form">
      <Input label="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
      <Input label="Icon (emoji)" value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="💿" />
      <Input label="Spotify URL" value={form.spotify_url || ''} onChange={e => setForm(f => ({ ...f, spotify_url: e.target.value }))} placeholder="https://open.spotify.com/album/..." />

      <LanguageTabs currentLang={lang} onChange={setLang} />
      {lang === 'en' ? (
        <>
          <Input label="Metadata (EN)" value={form.meta_en || ''} onChange={e => setForm(f => ({ ...f, meta_en: e.target.value }))} placeholder="Album • 2023 • 9 songs" />
          <Textarea label="Note (EN, optional)" value={form.note_en || ''} onChange={e => setForm(f => ({ ...f, note_en: e.target.value }))} placeholder="Additional info..." rows={2} />
        </>
      ) : (
        <>
          <Input label="Metadata (RO)" value={form.meta_ro || ''} onChange={e => setForm(f => ({ ...f, meta_ro: e.target.value }))} placeholder="Album • 2023 • 9 piese" />
          <Textarea label="Note (RO, optional)" value={form.note_ro || ''} onChange={e => setForm(f => ({ ...f, note_ro: e.target.value }))} placeholder="Informații suplimentare..." rows={2} />
        </>
      )}

      <Input label="Sort Order" type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />

      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <Button onClick={() => onSave(form)}>Save</Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

export default AlbumsPage;
